import React, { useState, ReactElement, Fragment, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import DropZone from 'react-dropzone';
import { Link } from 'react-router-dom';
import { actions as baselineActions } from '@Actions/baselineActions';
import { actions as newProjectWizardActions } from '@Actions/newProjectWizardActions';
import * as types from '@Constants/actionTypes';
import { ImportError, NewProjectData } from '@Types/common';
import Notification from '@Common/Notification';
import './styles.scss';
import FileChooser from '@Components/Common/FileChooser';
import Icon from '@Components/Common/Icon';
import Drawer from '@Components/Common/Drawer/Drawer';
import ListDepartments from './components/ListDepartments/ListDepartments';
import ListJobTitles from './components/ListJobTitles/ListJobTitles';
import ListTaskTags from './components/ListTaskTags/ListTaskTags';
import ListPhases from './components/ListPhases/ListPhases';

interface ImportTasksProps {
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setProjectData: (func: React.Dispatch<React.SetStateAction<NewProjectData>>, skipIsDirty?: boolean) => any;
	projectKey: string;
	chooseDiffAddTaskMethod: () => void;
	setWizardSubStep?: (subStep: string) => void; // Nullable
	importType: string;
	selectedBaselineId?: number;
}

const ImportTasks = ({
	setIsLoading,
	setProjectData,
	projectKey,
	chooseDiffAddTaskMethod,
	setWizardSubStep,
	importType,
	selectedBaselineId = 0,
}: ImportTasksProps): ReactElement => {
	const dispatch: Dispatch = useDispatch();
	const importTemplate = async (templateType: string) => dispatch(baselineActions.importTemplate(templateType));
	const importNewBaseline = async (projectKey: string, file: File, importType: string, baselineId?: number) =>
		dispatch(newProjectWizardActions.import(projectKey, file, baselineId, importType));

	const [showPasteSpecial, setShowPasteSpecial] = useState<boolean>(false);
	const [isDropZoneActive, setIsDropZoneActive] = useState<boolean>(false);
	const [baselineId, setBaselineId] = useState<number>(selectedBaselineId);
	const [importErrors, setImportErrors] = useState<Array<ImportError>>([]);
	const [showRelativeHelp, setShowRelativeHelp] = useState<boolean>(false);
	const [showStaticHelp, setShowStaticHelp] = useState<boolean>(false);
	const [isDepartmentsDrawerOpen, setIsDepartmentsDrawerOpen] = useState<boolean>(false);
	const [isJobTitlesDrawerOpen, setIsJobTitlesDrawerOpen] = useState<boolean>(false);
	const [isTaskTagsDrawerOpen, setIsTaskTagsDrawerOpen] = useState<boolean>(false);
	const [isPhasesDrawerOpen, setiIsPhasesDrawerOpen] = useState<boolean>(false);

	const hiddenBrowseButton = React.useRef(null);

	useEffect(() => {
		setBaselineId(selectedBaselineId);
	}, [selectedBaselineId]);

	const onDownload = async () => {
		importTemplate(importType).then((error) => {
			if (error) {
				Notification('error', '', error);
			}
		});
	};

	const onFileBrowseChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			doImport(file, importType);
		}
	};

	const onDropFile = (files) => {
		const droppedFile = files && files.length > 0 ? files[0] : null;

		if (droppedFile) {
			if (droppedFile.type != 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
				Notification('error', 'Invalid File Type', 'You must pick an Excel document with .xlsx extension');
				setIsDropZoneActive(false);
			} else {
				doImport(droppedFile, importType);
			}
		}
	};

	const doImport = async (importFile: File, fileImportType: string) => {
		setIsLoading(true);
		setImportErrors([]);

		try {
			const response = await importNewBaseline(projectKey, importFile, fileImportType, baselineId ? baselineId : undefined);
			if (response.type === types.NEW_PROJECT_WIZARD_IMPORT_BASELINE_SUCCESS) {
				if (response.payload.importErrors && response.payload.importErrors.length > 0) {
					Notification('error', 'Validation Errors', 'Please correct the errors in the Excel document and try again');
					setImportErrors(response.payload.importErrors);
					setIsLoading(false);
					setIsDropZoneActive(false);
					hiddenBrowseButton.current.value = ''; // Clear file from file browse

					// If a baselineId was returned we want to track it so the user can retry import with same baseline
					if (response.payload.baselineId) setBaselineId(response.payload.baselineId);
				} else if (response.payload.errors && response.payload.errors.length > 0) {
					Notification('error', 'Validation Errors', response.payload.errors[0].message);
					setIsLoading(false);
					setIsDropZoneActive(false);

					// If a baselineId was returned we want to track it so the user can retry import with same baseline
					if (response.payload.baselineId) setBaselineId(response.payload.baselineId);
				} else {
					Notification('success', 'Import Successful', '');

					// Set the baselineId we just saved. Skip the isDirty mechanism because the import process already saved the baseline
					setProjectData(
						(existing) => ({
							...existing,
							baselineIds: [response.payload.baselineId],
						}),
						true
					);

					if (setWizardSubStep) setWizardSubStep('c.2'); // Take user to next step
				}
			} else if (response.type === types.NEW_PROJECT_WIZARD_IMPORT_BASELINE_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				setIsLoading(false);
				setIsDropZoneActive(false);
			}
		} catch (error) {
			Notification('error', 'Error', error);
			setIsLoading(false);
			setIsDropZoneActive(false);
		}
	};

	const OnRelativeStaticClick = () => {
		if (importType === 'relative') {
			setShowRelativeHelp(true);
		} else if (importType === 'static') {
			setShowStaticHelp(true);
		}
	};

	return (
		<div className='import-tasks-content'>
			<div className='container'>
				<div className='row'>
					<div className='col-12 mb-5'>
						<div className='upload-panel'>
							<div className='upload-panel-top'>
								<div></div>
								<div>
									<input ref={hiddenBrowseButton} type='file' onChange={onFileBrowseChange} accept='.xlsx' />
									<button
										type='button'
										className='btn pacer61-primary-purple-btn'
										onClick={() => hiddenBrowseButton.current.click()}>
										<i className='fa-regular fa-upload'></i>&nbsp;&nbsp;Upload File
									</button>
								</div>
							</div>

							<div className='upload-panel-bottom'>
								<DropZone
									onDrop={onDropFile}
									onDragEnter={() => setIsDropZoneActive(true)}
									onDragLeave={() => setIsDropZoneActive(false)}
									multiple={false}
									accept={{
										'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
									}}>
									{({ getRootProps, getInputProps }) => (
										<div {...getRootProps()} className='dropzone'>
											<input {...getInputProps()} />

											{isDropZoneActive && (
												<div className='dropzone-overlay'>
													<div className='overlay-content'>
														<i className='fa-solid fa-cloud-arrow-up fa-4x'></i>
													</div>
												</div>
											)}

											<div className='dropzone-content'>
												<i className='fa-regular fa-file fa-3x'></i>
												<p>Drag and drop the Pacer import tool file here or select Upload File</p>
											</div>
										</div>
									)}
								</DropZone>
							</div>
						</div>
					</div>
				</div>
				<div className='row'>
					<div className='col-6'>
						<div className='steps-panel'>
							<div className='step-heading'>
								<div className='step-heading-title'>Options and Tips</div>
							</div>
							<button type='button' className='btn pacer61-link-btn' onClick={() => chooseDiffAddTaskMethod()}>
								<i className='fa-regular fa-chevron-left fa-lg'></i>Select Different Add Task Method
							</button>

							<button type='button' className='btn pacer61-link-btn' onClick={() => onDownload()}>
								{`Download ${importType.toUpperCase()} Date Template `} <i className='fa-regular fa-chevron-right fa-lg'></i>
							</button>

							<div className='step-one-details'>
								<div>
									<button type='button' className='btn pacer61-link-btn' onClick={() => setShowPasteSpecial(true)}>
										Tip: How to use Excel's Paste-Special option when copying tasks to the Pacer import tool{' '}
										<i className='fa-regular fa-chevron-right fa-lg'></i>
									</button>
									{showPasteSpecial && (
										<h5>{`Choose the 'Paste Special' option in Excel rather than the standard 'Paste' to maximize the template's functionality. Specifically, choosing 'Values Only' will allow any irregular values in the Excel file to be highlighted, empowering you to detect and resolve conflicts.`}</h5>
									)}

									<button type='button' className='btn pacer61-link-btn' onClick={() => OnRelativeStaticClick()}>
										Tip: What is the difference between a Static Date and Relative Date?{' '}
										<i className='fa-regular fa-chevron-right fa-lg'></i>
									</button>

									{showRelativeHelp && (
										<h5>
											This is the most common option in Pacer. Relative defines the timeframe for task completion based on the
											project's launch date. Based on this setting, Pacer will automatically generate task due dates relative to
											the project completion date. You'll designate the number of days before or after the project's completion
											date that each task should be completed. This completion date is set up in the final step!
										</h5>
									)}
									{showStaticHelp && (
										<h5>
											Add in exact dates for when each task should be complete. All teams will complete all project tasks on the
											exact same dates/timeline.
										</h5>
									)}
								</div>
							</div>

							<div className='step-heading'>
								<div className='step-heading-title'>Need Help?</div>
							</div>

							<button type='button' className='btn pacer61-link-btn' onClick={() => setIsDepartmentsDrawerOpen(true)}>
								Show me my current Departments <i className='fa-regular fa-chevron-right fa-lg'></i>
							</button>
							<button type='button' className='btn pacer61-link-btn' onClick={() => setIsJobTitlesDrawerOpen(true)}>
								Show me my current Job Titles <i className='fa-regular fa-chevron-right fa-lg'></i>
							</button>
							<button type='button' className='btn pacer61-link-btn' onClick={() => setIsTaskTagsDrawerOpen(true)}>
								Show me my current Task Tags <i className='fa-regular fa-chevron-right fa-lg'></i>
							</button>
							<button type='button' className='btn pacer61-link-btn' onClick={() => setiIsPhasesDrawerOpen(true)}>
								Show me my current Phases <i className='fa-regular fa-chevron-right fa-lg'></i>
							</button>
						</div>
					</div>
					<div className='col-6'>
						{importErrors.length > 0 && (
							<Fragment>
								<div className='break'></div>
								<div className='text-danger'>
									The following tasks are not importing correctly. Please review the messages below and resolve the issue in the
									Excel file. When you are done, please upload the file again. If you need to create new departments, phases, tags,
									or job titles, then{' '}
									<Link to={{ pathname: '/admin/home' }} target='_blank'>
										click here
									</Link>
									.
								</div>
								<div className='break'></div>
							</Fragment>
						)}
						{importErrors.map((importError, idx) => (
							<div className='row w-75' key={idx}>
								<div className='col-3'>
									<span className='text-nowrap'>Row {importError.rowNumber}</span>
								</div>
								<div className='col-3'>
									<span className='text-nowrap'>Column {importError.columnNumber}</span>
								</div>
								<div className='col-6'>
									<span className='text-center'>{importError.errorMessage}</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{isDepartmentsDrawerOpen && (
				<Drawer isWide={true} title='Departments' onCancelClick={() => setIsDepartmentsDrawerOpen(false)}>
					<ListDepartments />
				</Drawer>
			)}
			{isJobTitlesDrawerOpen && (
				<Drawer isWide={true} title='Job Titles' onCancelClick={() => setIsJobTitlesDrawerOpen(false)}>
					<ListJobTitles />
				</Drawer>
			)}
			{isTaskTagsDrawerOpen && (
				<Drawer isWide={true} title='Task Tags' onCancelClick={() => setIsTaskTagsDrawerOpen(false)}>
					<ListTaskTags />
				</Drawer>
			)}
			{isPhasesDrawerOpen && (
				<Drawer isWide={true} title='Phases' onCancelClick={() => setiIsPhasesDrawerOpen(false)}>
					<ListPhases />
				</Drawer>
			)}
		</div>
	);
};

export default ImportTasks;
